import React from 'react';
import { ChartLineIcon, TableIcon } from 'lucide-react';

import { Bloc } from '@mairistem/administres-utils';
import { Button, Dropdown } from '@mairistem/composants';

import BlocTableauStatistiques from './BlocTableauStatistiques';
import BlocGraphiqueStatistique from './BlocGraphiqueStatistique';

interface BlocStatistiquesProps {
  style?: React.CSSProperties;
}

const currentYear = new Date().getFullYear();

const yearOptions = Array.from({ length: 10 }, (_, i) => {
  const year = currentYear - i;
  return { key: year, text: String(year), value: year };
});

const BlocStatistiques = (props: BlocStatistiquesProps) => {
  const { style } = props;

  const [modeTableau, setModeTableau] = React.useState(true);
  const [selectedYearMin, setSelectedYearMin] = React.useState(currentYear - 5);
  const [selectedYearMax, setSelectedYearMax] = React.useState(currentYear);

  // biome-ignore lint/correctness/useExhaustiveDependencies: need
  React.useEffect(() => {
    if (selectedYearMin > selectedYearMax) {
      const availableOptions = yearOptions.filter(
        (option) => option.value <= selectedYearMax,
      );
      if (availableOptions.length > 0) {
        setSelectedYearMin(
          Math.min(...availableOptions.map((option) => option.value)),
        );
      }
    }
  }, [selectedYearMax]);

  const yearOptionsMin = React.useMemo(
    () => yearOptions.filter((option) => option.value <= selectedYearMax),
    [selectedYearMax],
  );

  const yearOptionsMax = React.useMemo(
    () => yearOptions.filter((option) => option.value >= selectedYearMin),
    [selectedYearMin],
  );

  const styleHeader = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5em',
    justifyContent: 'space-between',
    width: '100%',
    height: '2em',
  };

  // Statistiques sous forme de tableau
  if (modeTableau) {
    return (
      <Bloc
        icon="chart bar"
        header={
          <div style={styleHeader}>
            <div>
              <span>Statistiques sur l&apos;année </span>
              <Dropdown
                inline
                scrolling
                data-testid="bloc-statistiques-dropdown-annee"
                options={yearOptions}
                value={selectedYearMax}
                onChange={(_, data) => setSelectedYearMax(data.value as number)}
                style={{ fontWeight: 600 }}
              />
            </div>
            <Button
              icon={<ChartLineIcon size="1em" />}
              data-testid="bloc-statistiques-bouton-graphique"
              onClick={() => setModeTableau(false)}
              compact
            />
          </div>
        }
        style={style}
      >
        <BlocTableauStatistiques selectedYear={selectedYearMax} />
      </Bloc>
    );
  }

  // Statistiques sous forme de graphique
  return (
    <Bloc
      icon="chart bar"
      header={
        <div style={styleHeader}>
          <div>
            <span>Évolution du nombre de PACS entre </span>
            <Dropdown
              inline
              scrolling
              data-testid="bloc-statistiques-dropdown-annee-min"
              options={yearOptionsMin}
              value={selectedYearMin}
              onChange={(_, data) => setSelectedYearMin(data.value as number)}
              style={{ fontWeight: 600 }}
            />
            <span>et </span>
            <Dropdown
              inline
              scrolling
              data-testid="bloc-statistiques-dropdown-annee-max"
              options={yearOptionsMax}
              value={selectedYearMax}
              onChange={(_, data) => setSelectedYearMax(data.value as number)}
              style={{ fontWeight: 600 }}
            />
          </div>
          <Button
            icon={<TableIcon size="1em" />}
            data-testid="bloc-statistiques-bouton-tableau"
            onClick={() => setModeTableau(true)}
            compact
          />
        </div>
      }
      style={style}
    >
      <BlocGraphiqueStatistique
        anneeMin={selectedYearMin}
        anneeMax={selectedYearMax}
      />
    </Bloc>
  );
};

export default BlocStatistiques;
